import { Button, Skeleton, Tooltip } from '@affine/component';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { useWorkspace } from '@affine/core/components/hooks/use-workspace';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import {
  type WorkspaceMetadata,
  type WorkspaceProfileInfo,
} from '@affine/core/modules/workspace';
import { UNTITLED_WORKSPACE_NAME } from '@affine/env/constant';
import { useI18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  CloudWorkspaceIcon,
  CollaborationIcon,
  CopyIcon,
  DoneIcon,
  InformationFillDuotoneIcon,
  LocalWorkspaceIcon,
  NoNetworkIcon,
  SettingsIcon,
  TeamWorkspaceIcon,
  UnsyncIcon,
} from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { LiveData, useLiveData } from '@toeverything/infra';
import clsx from 'clsx';
import type { HTMLAttributes } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Observable } from 'rxjs';

import { useCatchEventCallback } from '../../hooks/use-catch-event-hook';
import { shouldShowGlobalSyncRetryStatus } from '../../top-tip-sync-contract';
import { WorkspaceAvatar } from '../../workspace-avatar';
import { workspaceCardAvatarSize } from './avatar-size';
import * as styles from './styles.css';
import {
  getWorkspaceSyncDisplayProgress,
  getWorkspaceSyncRawProgress,
} from './sync-progress';
export { PureWorkspaceCard } from './pure-workspace-card';

const CloudWorkspaceStatus = () => {
  const t = useI18n();

  return (
    <>
      <CloudWorkspaceIcon />
      {t.t('com.streamlain.workspace.syncStatus.cloud')}
    </>
  );
};

const SyncingWorkspaceStatus = () => {
  const t = useI18n();

  return <>{t.t('com.streamlain.workspace.syncStatus.syncingLabel')}</>;
};

const UnSyncWorkspaceStatus = () => {
  const t = useI18n();

  return (
    <>
      <UnsyncIcon />
      {t.t('com.streamlain.workspace.syncStatus.waitForUpload')}
    </>
  );
};

const LocalWorkspaceStatus = () => {
  const t = useI18n();

  return (
    <>
      {!BUILD_CONFIG.isElectron ? (
        <InformationFillDuotoneIcon
          style={{ color: cssVarV2('status/error') }}
        />
      ) : (
        <LocalWorkspaceIcon />
      )}
      {t.t('com.streamlain.workspace.syncStatus.local')}
    </>
  );
};

const OfflineStatus = () => {
  const t = useI18n();

  return (
    <>
      <NoNetworkIcon />
      {t.t('com.streamlain.workspace.syncStatus.offline')}
    </>
  );
};

interface SyncEngineState {
  total: number;
  syncing: number;
  synced: boolean;
  syncRetrying: boolean;
  syncErrorMessage: string | null;
  syncIsolatedFailure?: unknown | null;
}

const useSyncEngineSyncProgress = (meta: WorkspaceMetadata) => {
  const t = useI18n();
  const connectivity = useConnectivityStatus();
  const workspace = useWorkspace(meta);
  const displayedProgressRef = useRef<number | undefined>(undefined);
  const progressWorkspaceIdRef = useRef<string | null>(null);

  const engineState = useLiveData(
    useMemo(() => {
      return workspace
        ? LiveData.from<SyncEngineState | null>(
            workspace.engine.doc.state$ as Observable<SyncEngineState>,
            null
          )
        : null;
    }, [workspace])
  );
  const showSyncRetrying = useUiOfflineSignal(
    shouldShowGlobalSyncRetryStatus(engineState)
  );

  if (!engineState || !workspace) {
    return null;
  }

  if (progressWorkspaceIdRef.current !== meta.id) {
    displayedProgressRef.current = undefined;
    progressWorkspaceIdRef.current = meta.id;
  }

  const rawProgress = getWorkspaceSyncRawProgress(engineState);
  const hasPendingSync = engineState.syncing > 0;
  const progress = getWorkspaceSyncDisplayProgress(
    displayedProgressRef.current,
    rawProgress,
    hasPendingSync
  );
  displayedProgressRef.current = progress;
  const backendReachable = !connectivity.offline;

  let content;
  if (workspace.flavour === 'local') {
    if (!BUILD_CONFIG.isElectron) {
      content = t.t('com.streamlain.workspace.syncStatus.localDemo');
    } else {
      content = t.t('com.streamlain.workspace.syncStatus.savedLocally');
    }
  } else if (!backendReachable) {
    content = t.t('com.streamlain.workspace.syncStatus.disconnected');
  } else if (showSyncRetrying && engineState.syncErrorMessage) {
    content = t.t('com.streamlain.workspace.syncStatus.reconnectingWithError', {
      message: engineState.syncErrorMessage,
    });
  } else if (showSyncRetrying) {
    content = t.t('com.streamlain.workspace.syncStatus.reconnecting');
  } else if (hasPendingSync) {
    content =
      progress === undefined
        ? t.t('com.streamlain.workspace.syncStatus.syncing')
        : t.t('com.streamlain.workspace.syncStatus.syncingWithProgress', {
            percent: Math.floor(progress * 100),
          });
  } else {
    content = t.t('com.streamlain.workspace.syncStatus.synced');
  }

  const CloudWorkspaceSyncStatus = () => {
    if (showSyncRetrying) {
      return <UnSyncWorkspaceStatus />;
    } else if (hasPendingSync) {
      return <SyncingWorkspaceStatus />;
    } else {
      return <CloudWorkspaceStatus />;
    }
  };

  return {
    message: content,
    icon:
      workspace.flavour !== 'local' ? (
        !backendReachable ? (
          <OfflineStatus />
        ) : (
          <CloudWorkspaceSyncStatus />
        )
      ) : (
        <LocalWorkspaceStatus />
      ),
    progress,
    active:
      workspace.flavour !== 'local' &&
      backendReachable &&
      (hasPendingSync || showSyncRetrying),
  };
};

const usePauseAnimation = (timeToResume = 5000) => {
  const [paused, setPaused] = useState(false);

  const resume = useCallback(() => {
    setPaused(false);
  }, []);

  const pause = useCallback(() => {
    setPaused(true);
    if (timeToResume > 0) {
      setTimeout(resume, timeToResume);
    }
  }, [resume, timeToResume]);

  return { paused, pause };
};

const SyncStatusTooltipContent = ({ message }: { message: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      void navigator.clipboard.writeText(message).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    },
    [message]
  );

  return (
    <div className={styles.syncTooltipContent}>
      <span className={styles.syncTooltipMessage}>{message}</span>
      <button
        className={styles.syncTooltipCopyButton}
        onClick={handleCopy}
        title="Copy to clipboard"
      >
        {copied ? (
          <DoneIcon width={12} height={12} />
        ) : (
          <CopyIcon width={12} height={12} />
        )}
      </button>
    </div>
  );
};

const WorkspaceSyncInfo = ({
  workspaceMetadata,
  workspaceProfile,
  dense,
}: {
  workspaceMetadata: WorkspaceMetadata;
  workspaceProfile: WorkspaceProfileInfo;
  dense?: boolean;
}) => {
  const syncStatus = useSyncEngineSyncProgress(workspaceMetadata);
  const isCloud = workspaceMetadata.flavour !== 'local';
  const { paused, pause } = usePauseAnimation();

  // to make sure that animation will play first time
  const [delayActive, setDelayActive] = useState(false);
  useEffect(() => {
    if (paused || !syncStatus) {
      return;
    }
    const delayOpen = 0;
    const delayClose = 200;
    let timer: ReturnType<typeof setTimeout>;
    if (syncStatus.active) {
      timer = setTimeout(() => {
        setDelayActive(syncStatus.active);
      }, delayOpen);
    } else {
      timer = setTimeout(() => {
        setDelayActive(syncStatus.active);
        pause();
      }, delayClose);
    }
    return () => clearTimeout(timer);
  }, [pause, paused, syncStatus]);

  if (!workspaceProfile) {
    return null;
  }

  return (
    <div
      className={styles.workspaceInfoSlider}
      data-active={delayActive}
      data-dense={dense}
    >
      <div className={styles.workspaceInfoSlide}>
        <div className={styles.workspaceInfo} data-type="normal">
          <div className={styles.workspaceName} data-testid="workspace-name">
            {workspaceProfile.name}
          </div>
          {!dense ? (
            <div className={styles.workspaceStatus}>
              {isCloud ? (
                syncStatus && !syncStatus.active ? (
                  syncStatus.icon
                ) : (
                  <CloudWorkspaceStatus />
                )
              ) : (
                <LocalWorkspaceStatus />
              )}
            </div>
          ) : null}
        </div>

        {/* when syncing/offline/... */}
        {syncStatus && (
          <div className={styles.workspaceInfo} data-type="events">
            <Tooltip
              content={
                <SyncStatusTooltipContent message={syncStatus.message} />
              }
              options={{ className: styles.workspaceInfoTooltip }}
            >
              <div className={styles.workspaceActiveStatus}>
                {syncStatus.icon}
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export const WorkspaceCard = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    workspaceMetadata: WorkspaceMetadata;
    showSyncStatus?: boolean;
    showArrowDownIcon?: boolean;
    avatarSize?: number;
    disable?: boolean;
    hideCollaborationIcon?: boolean;
    hideTeamWorkspaceIcon?: boolean;
    active?: boolean;
    infoClassName?: string;
    dense?: boolean;
    onClickOpenSettings?: (workspaceMetadata: WorkspaceMetadata) => void;
    onClickEnableCloud?: (workspaceMetadata: WorkspaceMetadata) => void;
  }
>(
  (
    {
      workspaceMetadata,
      showSyncStatus,
      showArrowDownIcon,
      onClickOpenSettings,
      onClickEnableCloud,
      className,
      infoClassName,
      disable,
      hideCollaborationIcon,
      hideTeamWorkspaceIcon,
      active,
      dense,
      avatarSize = workspaceCardAvatarSize(dense),
      ...props
    },
    ref
  ) => {
    const t = useI18n();
    const information = useWorkspaceInfo(workspaceMetadata);

    const name = information?.name ?? UNTITLED_WORKSPACE_NAME;

    const onEnableCloud = useCatchEventCallback(() => {
      onClickEnableCloud?.(workspaceMetadata);
    }, [onClickEnableCloud, workspaceMetadata]);

    const onOpenSettings = useCatchEventCallback(() => {
      onClickOpenSettings?.(workspaceMetadata);
    }, [onClickOpenSettings, workspaceMetadata]);

    return (
      <div
        className={clsx(
          styles.container,
          disable ? styles.disable : null,
          className
        )}
        role="button"
        tabIndex={0}
        data-testid="workspace-card"
        ref={ref}
        {...props}
      >
        <div className={clsx(styles.infoContainer, infoClassName)}>
          {information ? (
            <WorkspaceAvatar
              meta={workspaceMetadata}
              rounded={3}
              data-testid="workspace-avatar"
              size={avatarSize}
              name={name}
              colorfulFallback
            />
          ) : (
            <Skeleton width={avatarSize} height={avatarSize} />
          )}
          <div className={styles.workspaceTitleContainer}>
            {information ? (
              showSyncStatus ? (
                <WorkspaceSyncInfo
                  workspaceProfile={information}
                  workspaceMetadata={workspaceMetadata}
                  dense={dense}
                />
              ) : (
                <span className={styles.workspaceName}>{information.name}</span>
              )
            ) : (
              <Skeleton width={100} />
            )}
          </div>
          <div className={styles.showOnCardHover}>
            {onClickEnableCloud && workspaceMetadata.flavour === 'local' ? (
              <Button
                className={styles.enableCloudButton}
                onClick={onEnableCloud}
              >
                {t.t('com.streamlain.workspace.syncStatus.enableCloud')}
              </Button>
            ) : null}

            {onClickOpenSettings && (
              <div className={styles.settingButton} onClick={onOpenSettings}>
                <SettingsIcon width={16} height={16} />
              </div>
            )}
          </div>
        </div>

        <div className={styles.suffixIcons}>
          {hideCollaborationIcon || information?.isOwner ? null : (
            <Tooltip
              content={t['com.streamlain.settings.workspace.state.joined']()}
            >
              <CollaborationIcon className={styles.collaborationIcon} />
            </Tooltip>
          )}
          {hideTeamWorkspaceIcon || !information?.isTeam ? null : (
            <Tooltip
              content={t['com.streamlain.settings.workspace.state.team']()}
            >
              <TeamWorkspaceIcon className={styles.collaborationIcon} />
            </Tooltip>
          )}
          {active && (
            <div className={styles.activeContainer}>
              <DoneIcon className={styles.activeIcon} />
            </div>
          )}
          {showArrowDownIcon && <ArrowDownSmallIcon />}
        </div>
      </div>
    );
  }
);

WorkspaceCard.displayName = 'WorkspaceCard';
